import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PublicPageTemplate } from '@/components/public';
import { getCanonicalUrl } from '@/config/site';
import { createBreadcrumbSchema, createOrganizationSchema, createProductSchema } from "@/config/seo";
import { getProductBySlug } from '@/data/products/registry';
import { UserService } from '@/services/users/UserService';
import { AfterHoursHero } from './components/AfterHoursHero';
import { RealWorldScenarios } from './components/RealWorldScenarios';
import { AfterHoursOpportunitiesShowcase } from './components/AfterHoursOpportunitiesShowcase';
import { AfterHoursFeatures } from './components/AfterHoursFeatures';
import HomeUserManual from '@/pages/Home/HomeUserManual';
import { AfterHoursGenieCTA } from './components/AfterHoursGenieCTA';
import { YourGenieYourWay } from './components/YourGenieYourWay';
import { AfterHoursCTA } from './components/AfterHoursCTA';

const AfterHoursPage = () => {
  const navigate = useNavigate();
  const product = getProductBySlug('after-hours');
  const [activeWordIndex, setActiveWordIndex] = useState(0);

  const ROTATING_WORDS = [
    'call',
    'opportunity',
    'lead',
    'customer',
  ];

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Rotate words every 3.5 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveWordIndex((prev) => (prev + 1) % ROTATING_WORDS.length);
    }, 3500);
    return () => clearInterval(timer);
  }, [ROTATING_WORDS.length]);

  if (!product) {
    return (
      <div className="min-h-screen bg-[#F9FAFC] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-semibold mb-2">Product not found</h2>
          <button
            onClick={() => navigate('/products')}
            className="text-purple-600 hover:text-purple-700 font-medium"
          >
            Back to Products
          </button>
        </div>
      </div>
    );
  }

  const handleGetStarted = async () => {
    const { data: { user } } = await UserService.getUser();
    if (!user) {
      navigate('/signin');
      return;
    }
    navigate('/genie-hub/genie-setup');
  };

  const seoConfig = {
    title: `${product.meta.name} | Help Genie`,
    description: product.meta.description,
    ogTitle: product.meta.name,
    ogDescription: product.meta.description,
    canonicalUrl: getCanonicalUrl(`/products/${product.meta.slug}`),
    structuredData: [
      createBreadcrumbSchema([
        { name: "Home", url: getCanonicalUrl("/") },
        { name: "Products", url: getCanonicalUrl("/products") },
        { name: product.meta.name, url: getCanonicalUrl(`/products/${product.meta.slug}`) },
      ]),
      createProductSchema(product.meta.name, product.meta.description, getCanonicalUrl(`/products/${product.meta.slug}`)),
      createOrganizationSchema(),
    ],
  };

  return (
    <PublicPageTemplate seo={seoConfig}>
      <AfterHoursHero
        product={product}
        activeWordIndex={activeWordIndex}
        rotatingWords={ROTATING_WORDS}
        onGetStarted={handleGetStarted}
      />

      <RealWorldScenarios />

      <div className="bg-gradient-to-b from-white via-purple-50/30 to-white">
        <AfterHoursGenieCTA />
      </div>

      <AfterHoursOpportunitiesShowcase />

      <HomeUserManual />

      {/* Hidden for now */}
      {/* <AfterHoursFeatures /> */}

      <YourGenieYourWay />

      <AfterHoursCTA />
    </PublicPageTemplate>
  );
};

export default AfterHoursPage;
